import { Injectable, inject } from '@angular/core';
import {
  AutoQuoteStorageStateKey,
  LegArray,
  RfqTab,
  RfqTabLegs,
  RfqTabPosition
} from '@approot/models';
import {
  AuthService,
  ExecutionContext,
  RfqService,
  RFQTabTitle,
  UserStorageService,
  StrategiesService,
  RfqDetails,
  ClientQuoteEventExtended
} from '@approot/services';
import { BehaviorSubject, Observable } from 'rxjs';
import { ProviderMultiHedge, ProviderQuoteEvent } from './provider-quote.model';
import { getLegClientActionsHeader } from '@approot/services/rfq/rfq-tab/rfq-tab';
import { isFirstGenExotic } from '@approot/services/rfq/rfq-creation';

export interface TabItem {
  requestId: number;
  title?: {
    quotePair: {
      base: string;
      term: string;
    };
    productName: RFQTabTitle;
    status: { statusType: any; statusExtra: any };
  };
  legs?: RfqTabLegs[];
  originalLegs?: Record<string, any>[];
  isFirstGenExotic?: boolean;
  dimensions?: { height: number; width: number };
  minDimensions?: {
    height: number;
    width: number;
    heightWithTopOfBook: number;
  };
  maxDimensions?: { height: number; width: number };
  positions?: RfqTabPosition;
  topOfBook?: boolean;
  banksActive?: number;
  quoteOptions?: {
    showPayRecieve: boolean;
    showQuotedPremiumSection: boolean;
  };
  fullQuote: ProviderQuoteEvent;
  isVolBased?: boolean;
  isPremiumBasedVanillasWithSpotFeed?: boolean;
  isPremiumBased?: boolean;
  executionContext?: ExecutionContext;
  rfqDetails?: RfqDetails;
  useWhiteLabelMargin?: boolean;
  streamedHedge?: ProviderMultiHedge[];
}

export interface GetWhiteLabelLastClientQuoteEvent {
  response: {
    event: ClientQuoteEventExtended;
    requestId: null;
    timestamp: number;
  };
}

export interface GetLiquidityProviderEventsResponse {
  response: {
    timestamp: number;
    result: any[];
  };
}

@Injectable({
  providedIn: 'root'
})
export class ProviderQuoteService {
  public readonly quotes$: Observable<RfqTab[]>;

  public readonly tabs: TabItem[] = []; // stores all the tab information
  private readonly quotesSubject = new BehaviorSubject<any[]>(null);
  private userStorageService = inject(UserStorageService);

  constructor(
    private strategiesService: StrategiesService,
    private rfqService: RfqService,
    private authService: AuthService
  ) {
    this.quotes$ = this.quotesSubject.asObservable();
  }

  public quoteExists(quote: ProviderQuoteEvent) {
    const id = quote.quote.request.requestId;
    return !!this.tabs.find(tab => tab.requestId === id);
  }

  public addNewTab(
    quoteEvent: ProviderQuoteEvent,
    originalLegs: Record<string, any>[],
    rfqDetails: RfqDetails
  ) {
    const tab: TabItem = {
      requestId: quoteEvent.quote.request.requestId,
      originalLegs,
      fullQuote: quoteEvent
    };

    if (rfqDetails) {
      tab.rfqDetails = rfqDetails;
    }
    this.tabs.push(tab);
    this.quotesSubject.next(this.tabs);
    if (this.tabs.length === 1) {
      this.strategiesService.resizeStrategyComponent();
    }
  }

  public removeTabById(requestId: number) {
    const idx = this.tabs.findIndex(tab => tab.requestId === requestId);
    if (idx !== -1) {
      this.removeTabByIndex(idx);
    }
  }

  public removeAllTabs() {
    this.tabs.splice(0);
  }

  public removeTabByIndex(idx: number) {
    this.tabs.splice(idx, 1);
    this.quotesSubject.next(this.tabs);
  }

  public updateTab(quoteEvent: ProviderQuoteEvent, legs: LegArray): TabItem {
    const { base, term } = quoteEvent.quote.request.quotePair;

    const leg0 = legs[0];
    const productName = {
      shortName: leg0.leg.productType.toLowerCase(),
      longName: leg0.leg.productType
    };
    const newRfqTab = {
      requestId: quoteEvent.quote.request.requestId,
      title: {
        quotePair: {
          base,
          term
        },
        productName: productName,
        status: { statusType: null, statusExtra: null }
      },
      legs: legs.map(leg => {
        return {
          leg: leg,
          shortName: productName.shortName
        };
      }),
      isFirstGenExotic: isFirstGenExotic(productName?.shortName),
      dimensions: { height: 810, width: 660 },
      minDimensions: { height: null, width: null, heightWithTopOfBook: null },
      maxDimensions: { height: null, width: null },
      positions: getLegClientActionsHeader(
        legs.map(x => x.leg.preSetPosition.position)
      ),
      topOfBook: false,
      oneWayView: {
        oneWay: false,
        sideToShow: 'bid'
      },
      quoteOptions: {
        showPayRecieve: true,
        showQuotedPremiumSection: true
      },
      banksActive: 0,
      fullQuote: quoteEvent
    };
    return newRfqTab;
  }

  public extractHydraProductsFromMedusaProviderQuoteEvent(
    quote: ProviderQuoteEvent
  ): Observable<any[]> {
    const payload = {
      task: 'extractHydraProductsFromMedusaProviderQuoteEvent',
      item: quote
    };

    return this.rfqService.tradeService<any[]>(payload);
  }

  public getLiquidityProviderEvents(
    requestIds: number[],
    timestamp: number
  ): Observable<GetLiquidityProviderEventsResponse> {
    const payload = {
      task: 'getLiquidityProviderEvents',
      requestIds,
      timestamp
    };
    return this.authService.fxPost<GetLiquidityProviderEventsResponse>(
      payload,
      `whiteLabelServices`,
      `task=${payload.task}`
    );
  }

  public getWhiteLabelLastClientQuoteEvent(
    requestId: number,
    provider: string
  ): Observable<GetWhiteLabelLastClientQuoteEvent> {
    const payload = {
      task: 'getWhiteLabelLastClientQuoteEvent',
      requestId,
      provider
    };
    return this.authService.fxPost<GetWhiteLabelLastClientQuoteEvent>(
      payload,
      `whiteLabelServices`,
      `task=${payload.task}`
    );
  }

  public setAutoQuoteState(state: boolean) {
    this.userStorageService.setItem(
      AutoQuoteStorageStateKey,
      state ? 'y' : 'n'
    );
  }
}
